import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ClientDashboard.css';
import DocumentViewer from '../DocumentViewer';
import LoadingOverlay from '../LoadingOverlay';
import '../DocumentViewer.css';
import griffsoLogo from '../assets/griffso.png';

const API = 'https://ai.griffso.com/api/parsing';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const clientId = sessionStorage.getItem('client_id');
  const clientName = sessionStorage.getItem('client_name');
  const token = sessionStorage.getItem('client_token');

  const [activeSection, setActiveSectionRaw] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    const valid = ['overview','parsing','reports','api','tester','billing','documents','settings','help'];
    return valid.includes(hash) ? hash : 'overview';
  });
  const setActiveSection = (s) => { setActiveSectionRaw(s); window.location.hash = s; };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [profile, setProfile] = useState(null);
  const [apiLogs, setApiLogs] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [dailyUsage, setDailyUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tester, setTester] = useState({
    method: 'POST', url: `${API}/auth`,
    headers: [{ key: 'Content-Type', value: 'application/json', enabled: true }],
    body: '', bodyType: 'json',
    formFields: [{ key: '', value: '', type: 'text', file: null }],
    sending: false, response: null, _lastToken: '',
  });
  const [testerTab, setTesterTab] = useState('body');
  const [respTab, setRespTab] = useState('body');

  // Document Parsing state
  const [parseToken, setParseToken] = useState('');
  const [parseDocType, setParseDocType] = useState('');
  const [parsePdfCat, setParsePdfCat] = useState('RESUME');
  const [parseFile, setParseFile] = useState(null);
  const [parseFront, setParseFront] = useState(null);
  const [parseBack, setParseBack] = useState(null);
  const [parsePreviews, setParsePreviews] = useState({ file: null, front: null, back: null });
  const [parseResults, setParseResults] = useState(null);
  const [parseLoading, setParseLoading] = useState(false);
  const [parseError, setParseError] = useState('');
  const [parseStatus, setParseStatus] = useState('');

  const tokenRef = useRef(token);
  const clientIdRef = useRef(clientId);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API}/client/me?client_id=${clientIdRef.current}`, { headers: { Authorization: `Bearer ${tokenRef.current}` } });
      const data = await res.json();
      if (!data.error) setProfile(data);
    } catch { /* silent */ }
  }, []);

  const fetchApiLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/client/api-logs?client_id=${clientIdRef.current}`, { headers: { Authorization: `Bearer ${tokenRef.current}` } });
      const data = await res.json();
      if (!data.error) setApiLogs(data.logs || []);
    } catch { /* silent */ }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/client/documents?client_id=${clientIdRef.current}`, { headers: { Authorization: `Bearer ${tokenRef.current}` } });
      const data = await res.json();
      if (!data.error) setDocuments(data.documents || []);
    } catch { /* silent */ }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/client/transactions?client_id=${clientIdRef.current}`, { headers: { Authorization: `Bearer ${tokenRef.current}` } });
      const data = await res.json();
      if (!data.error) setTransactions(data);
    } catch { /* silent */ }
  }, []);

  const fetchDailyUsage = useCallback(async () => {
    try {
      const res = await fetch(`${API}/client/daily-usage?client_id=${clientIdRef.current}`, { headers: { Authorization: `Bearer ${tokenRef.current}` } });
      const data = await res.json();
      if (!data.error) setDailyUsage(data.daily || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!token || !clientId) { navigate('/parsingmodel', { replace: true }); return; }
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchProfile(), fetchApiLogs(), fetchDocuments(), fetchTransactions(), fetchDailyUsage(), fetchParsedData()]);
      setLoading(false);
    };
    load();
  }, [navigate, token, clientId]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('client_token');
    sessionStorage.removeItem('client_id');
    sessionStorage.removeItem('client_name');
    parseTokenRef.current = '';
    navigate('/parsingmodel');
  };

  const formatPrice = (p) => p === 0 ? 'Free' : `₹${(p / 100).toLocaleString()}`;
  const statusColor = (sc) => sc < 300 ? '#22c55e' : sc < 400 ? '#f59e0b' : '#ef4444';

  const p = profile || {};
  const tenant = p.client || {};
  const stats = p.stats || {};
  const plan = tenant.plan_def || {};
  const usagePct = tenant.api_limit === -1 ? 0 : Math.min(100, ((tenant.api_used || 0) / (tenant.api_limit || 1)) * 100);
  const apiEnabled = tenant.api_integration_enabled !== false;

  const navItems = [
    { id: 'overview', icon: '📊', label: 'Overview' },
    { id: 'parsing', icon: '🔍', label: 'Document Parsing' },
    { id: 'reports', icon: '📊', label: 'Data & Reports' },
    { id: 'api', icon: '📡', label: 'API Details' },
    { id: 'tester', icon: '🛠️', label: 'Dev Tools' },
    { id: 'billing', icon: '💳', label: 'Plan & Payment' },
    { id: 'documents', icon: '📄', label: 'Documents' },
    { id: 'settings', icon: '⚙️', label: 'Settings' },
    { id: 'help', icon: '❓', label: 'Help' },
  ];

  const allFeatures = ['PAN','AADHAAR','DRIVING','VOTER','RESUME','NOTICE','SUMMARY','BANK_STATEMENT'];

  const renderOverview = () => (
    <div className="dash-section">
      <div className="stats-grid">
        {[
          { icon: '📡', label: 'Total API Calls', value: (stats.total_api_calls || 0).toLocaleString(), color: '#6366f1' },
          { icon: '📄', label: 'Documents Processed', value: (stats.total_docs || 0).toLocaleString(), color: '#22c55e' },
          { icon: '❌', label: 'Errors', value: (stats.total_errors || 0).toLocaleString(), color: '#ef4444' },
          { icon: '⚡', label: 'Avg Response', value: `${stats.avg_response_ms || 0}ms`, color: '#f59e0b' },
          { icon: '💎', label: 'Plan', value: (plan.label || (tenant.plan || 'free')).toUpperCase(), color: '#8b5cf6' },
          { icon: '🔄', label: 'API Remaining', value: tenant.api_limit === -1 ? '∞' : Math.max(0, (tenant.api_limit || 0) - (tenant.api_used || 0)).toLocaleString(), color: usagePct > 90 ? '#ef4444' : '#06b6d4' },
        ].map((s, i) => (
          <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
            <div className="stat-card-glow" />
            <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
            <div className="stat-info">
              <span className="stat-value">{s.value}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dash-grid-2">
        {/* Account & Plan */}
        <div className="glass-panel">
          <div className="panel-head"><span className="panel-icon">🏢</span><h3>Account & Plan</h3></div>
          <div className="perf-metrics">
            <div className="perf-row"><span className="perf-label">Client ID</span><span className="perf-value mono-text">{clientId}</span></div>
            <div className="perf-row"><span className="perf-label">Name</span><span className="perf-value">{tenant.name || clientName}</span></div>
            <div className="perf-row"><span className="perf-label">Plan</span><span className={`plan-badge ${tenant.plan || 'free'}`}>{(plan.label || (tenant.plan || 'free')).toUpperCase()}</span></div>
            <div className="perf-row"><span className="perf-label">Billing Cycle</span><span className="perf-value">{tenant.billing_cycle === 'yearly' ? '📅 Yearly' : '📅 Monthly'}</span></div>
            <div className="perf-row"><span className="perf-label">Plan Start</span><span className="perf-value">{tenant.plan_start || '—'}</span></div>
            <div className="perf-row"><span className="perf-label">Plan End</span><span className="perf-value">{tenant.plan_end || '—'}</span></div>
            <div className="perf-row"><span className="perf-label">Payment Status</span><span className={`pay-badge ${tenant.payment_status || 'trial'}`}>{(tenant.payment_status || 'trial').toUpperCase()}</span></div>
            <div className="perf-row"><span className="perf-label">Account Status</span><span className={`status-dot ${tenant.active ? 'active' : 'inactive'}`}>{tenant.active ? '✅ Active' : '❌ Inactive'}</span></div>
          </div>
        </div>

        {/* API Usage */}
        <div className="glass-panel">
          <div className="panel-head"><span className="panel-icon">📡</span><h3>API Usage</h3></div>
          <div className="perf-metrics">
            <div className="perf-row"><span className="perf-label">API Limit</span><span className="perf-value">{tenant.api_limit === -1 ? '♾️ Unlimited' : (tenant.api_limit || 0).toLocaleString()}</span></div>
            <div className="perf-row"><span className="perf-label">API Used</span><span className="perf-value">{(tenant.api_used || 0).toLocaleString()}</span></div>
            <div className="perf-row"><span className="perf-label">Remaining</span><span className="perf-value" style={{ color: usagePct > 90 ? '#fca5a5' : undefined }}>{tenant.api_limit === -1 ? '∞' : Math.max(0, (tenant.api_limit || 0) - (tenant.api_used || 0)).toLocaleString()}</span></div>
            <div className="perf-row">
              <span className="perf-label">Usage</span>
              <div className="api-usage-cell" style={{ minWidth: 120 }}>
                <div className="usage-bar-track"><div className="usage-bar-fill" style={{ width: `${tenant.api_limit === -1 ? 30 : usagePct}%`, background: usagePct > 90 ? 'linear-gradient(90deg,#ef4444,#f59e0b)' : undefined }} /></div>
                <span className="usage-text">{tenant.api_limit === -1 ? '∞' : `${Math.round(usagePct)}%`}</span>
              </div>
            </div>
            <div className="perf-row"><span className="perf-label">Dev Tools</span><span className={`status-dot ${apiEnabled ? 'active' : 'inactive'}`}>{apiEnabled ? '✅ Enabled' : '❌ Disabled'}</span></div>
            <div className="perf-row"><span className="perf-label">API Key</span><span className="perf-value mono-text" style={{ fontSize: '.7rem' }}>{tenant.key ? `${tenant.key.slice(0, 20)}...` : '—'}</span></div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="glass-panel">
        <div className="panel-head"><span className="panel-icon">🔐</span><h3>Features & Capabilities</h3></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '4px 0' }}>
          {allFeatures.map(f => {
            const enabled = (tenant.features || []).includes(f);
            return (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 12, background: enabled ? 'rgba(34,197,94,.08)' : 'rgba(255,255,255,.03)', border: `1px solid ${enabled ? 'rgba(34,197,94,.2)' : 'rgba(255,255,255,.06)'}`, minWidth: 140 }}>
                <span style={{ fontSize: '1.1rem' }}>{enabled ? '✅' : '🔒'}</span>
                <div>
                  <div style={{ fontSize: '.85rem', fontWeight: 700, color: enabled ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.3)' }}>{f}</div>
                  <div style={{ fontSize: '.72rem', color: enabled ? '#86efac' : 'rgba(255,255,255,.2)' }}>{enabled ? 'Enabled' : 'Not Available'}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Doc Types Breakdown */}
      <div className="dash-grid-2">
        <div className="glass-panel">
          <div className="panel-head"><span className="panel-icon">📑</span><h3>Document Types Processed</h3></div>
          <div className="perf-metrics">
            {stats.doc_types && Object.keys(stats.doc_types).length > 0
              ? Object.entries(stats.doc_types).map(([dt, cnt]) => (
                <div key={dt} className="perf-row"><span className="perf-label">{dt}</span><span className="perf-value">{cnt}</span></div>
              ))
              : <div className="perf-row"><span className="perf-label text-muted">No documents yet</span></div>
            }
          </div>
        </div>

        {/* Contact Info */}
        <div className="glass-panel">
          <div className="panel-head"><span className="panel-icon">📧</span><h3>Contact Info</h3></div>
          <div className="perf-metrics">
            <div className="perf-row"><span className="perf-label">Email</span><span className="perf-value">{tenant.contact_email || '—'}</span></div>
            <div className="perf-row"><span className="perf-label">Phone</span><span className="perf-value">{tenant.contact_phone || '—'}</span></div>
            <div className="perf-row"><span className="perf-label">Address</span><span className="perf-value">{tenant.company_address || '—'}</span></div>
          </div>
        </div>
      </div>

      {/* Daily Usage Chart */}
      {dailyUsage && dailyUsage.length > 0 && (
        <div className="glass-panel">
          <div className="panel-head"><span className="panel-icon">📅</span><h3>Daily API Usage (Last 30 Days)</h3></div>
          <div className="usage-chart">
            {dailyUsage.slice(-30).map((d) => {
              const maxVal = Math.max(...dailyUsage.map(x => x.calls || 0), 1);
              const day = d.day || d.date || '';
              return (
                <div key={day} className="usage-chart-bar">
                  <div className="usage-chart-track">
                    <div className="usage-chart-fill" style={{ height: `${Math.min(100, ((d.calls || 0) / maxVal) * 100)}%` }} />
                  </div>
                  <span className="usage-chart-label">{day.slice(5)}</span>
                  <span className="usage-chart-count">{d.calls}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const renderAPI = () => {
    if (!apiEnabled) return (
      <div className="dash-section">
        <div className="glass-panel" style={{ padding: 50, textAlign: 'center' }}>
          <span style={{ fontSize: '3.5rem', display: 'block', marginBottom: 16 }}>🚫</span>
          <h3 style={{ margin: '0 0 10px', fontSize: '1.2rem', color: 'rgba(255,255,255,.9)' }}>API Integration Not Enabled</h3>
          <p style={{ color: 'rgba(255,255,255,.45)', margin: '0 0 20px', maxWidth: 420, marginInline: 'auto', lineHeight: 1.6 }}>API access is currently disabled for your account. Please contact your administrator to enable API integration.</p>
          <span className="feature-chip" style={{ background: 'rgba(239,68,68,.12)', color: '#fca5a5', fontSize: '.85rem', padding: '8px 18px' }}>🔒 Contact Admin to Enable</span>
        </div>
      </div>
    );
    return (
    <div className="dash-section">
      <div className="stats-grid">
        {[
          { icon: '📡', label: 'Total Calls', value: (stats.total_api_calls || 0).toLocaleString(), color: '#6366f1' },
          { icon: '✅', label: 'Successful', value: ((stats.total_api_calls || 0) - (stats.total_errors || 0)).toLocaleString(), color: '#22c55e' },
          { icon: '❌', label: 'Failed', value: (stats.total_errors || 0).toLocaleString(), color: '#ef4444' },
          { icon: '⚡', label: 'Avg Response', value: `${stats.avg_response_ms || 0}ms`, color: '#f59e0b' },
        ].map((s, i) => (
          <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
            <div className="stat-card-glow" />
            <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
            <div className="stat-info">
              <span className="stat-value">{s.value}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="glass-panel">
        <div className="panel-head">
          <span className="panel-icon">📡</span><h3>Recent API Calls</h3>
          <button className="tenant-add-btn" style={{ fontSize: '.78rem', padding: '6px 14px' }} onClick={fetchApiLogs}>🔄 Refresh</button>
        </div>
        <div className="table-wrap" style={{ maxHeight: 500, overflow: 'auto' }}>
          <table className="dash-table" style={{ minWidth: 700 }}>
            <thead><tr><th>Time</th><th>Method</th><th>Endpoint</th><th>Status</th><th>Time (ms)</th><th>IP</th></tr></thead>
            <tbody>
              {(apiLogs || []).map((l, i) => (
                <tr key={i}>
                  <td><span className="text-muted">{l.created_at}</span></td>
                  <td><span className={`api-method method-${(l.method||'').toLowerCase()}`}>{l.method}</span></td>
                  <td><span className="mono-text">{l.endpoint}</span></td>
                  <td><span className="status-code-chip" style={{ '--sc-color': statusColor(l.status_code) }}>{l.status_code}</span></td>
                  <td>{l.response_time_ms || 0}</td>
                  <td><span className="text-muted">{l.ip_address}</span></td>
                </tr>
              ))}
              {(!apiLogs || apiLogs.length === 0) && <tr><td colSpan={6} className="no-data">No API calls yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* API Key Info */}
      <div className="glass-panel" style={{ marginTop: 20 }}>
        <div className="panel-head"><span className="panel-icon">🔑</span><h3>Your API Credentials</h3></div>
        <div className="perf-metrics">
          <div className="perf-row"><span className="perf-label">Client ID</span><span className="perf-value mono-text">{clientId}</span></div>
          <div className="perf-row"><span className="perf-label">API Key</span><span className="perf-value mono-text" style={{ fontSize: '.72rem' }}>{tenant.key || '—'}</span></div>
          <div className="perf-row"><span className="perf-label">API Integration</span><span className={`status-dot ${tenant.api_integration_enabled !== false ? 'active' : 'inactive'}`}>{tenant.api_integration_enabled !== false ? '✅ Enabled' : '❌ Disabled'}</span></div>
        </div>
      </div>
    </div>
    );
  };

  const renderBilling = () => {
    const txns = transactions?.transactions || [];
    const summary = transactions?.summary || {};
    return (
      <div className="dash-section">
        <div className="dash-grid-2">
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">💎</span><h3>Current Plan</h3></div>
            <div className="perf-metrics">
              <div className="perf-row"><span className="perf-label">Plan</span><span className={`plan-badge ${tenant.plan || 'free'}`}>{(plan.label || (tenant.plan || 'free')).toUpperCase()}</span></div>
              <div className="perf-row"><span className="perf-label">Billing Cycle</span><span className="perf-value">{tenant.billing_cycle === 'yearly' ? '📅 Yearly' : '📅 Monthly'}</span></div>
              <div className="perf-row"><span className="perf-label">Plan Start</span><span className="perf-value">{tenant.plan_start || '—'}</span></div>
              <div className="perf-row"><span className="perf-label">Plan End</span><span className="perf-value">{tenant.plan_end || '—'}</span></div>
              <div className="perf-row"><span className="perf-label">API Limit</span><span className="perf-value">{tenant.api_limit === -1 ? 'Unlimited' : (tenant.api_limit || 0).toLocaleString()}</span></div>
              <div className="perf-row"><span className="perf-label">API Used</span><span className="perf-value">{(tenant.api_used || 0).toLocaleString()}</span></div>
            </div>
          </div>
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">💰</span><h3>Payment Summary</h3></div>
            <div className="perf-metrics">
              <div className="perf-row"><span className="perf-label">Payment Status</span><span className={`pay-badge ${tenant.payment_status || 'trial'}`}>{(tenant.payment_status || 'trial').toUpperCase()}</span></div>
              <div className="perf-row"><span className="perf-label">Last Payment</span><span className="perf-value">{tenant.last_payment_date || '—'}</span></div>
              <div className="perf-row"><span className="perf-label">Total Paid</span><span className="revenue-text">{formatPrice(summary.total_paid || 0)}</span></div>
              <div className="perf-row"><span className="perf-label">Pending</span><span className="perf-value">{formatPrice(summary.total_pending || 0)}</span></div>
              <div className="perf-row"><span className="perf-label">Transactions</span><span className="perf-value">{summary.count || 0}</span></div>
            </div>
          </div>
        </div>

        {txns.length > 0 && (
          <div className="glass-panel" style={{ marginTop: 20 }}>
            <div className="panel-head"><span className="panel-icon">🧾</span><h3>Transaction History</h3></div>
            <div className="table-wrap" style={{ maxHeight: 400, overflow: 'auto' }}>
              <table className="dash-table">
                <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Description</th><th>Invoice</th><th>Method</th><th>Status</th></tr></thead>
                <tbody>
                  {txns.map((tx) => (
                    <tr key={tx.id}>
                      <td><span className="text-muted">{tx.transaction_date || tx.created_at}</span></td>
                      <td><span className={`pay-badge ${tx.type === 'refund' ? 'overdue' : tx.type === 'payment' ? 'paid' : 'pending'}`}>{tx.type.toUpperCase()}</span></td>
                      <td><span className="revenue-text">{formatPrice(tx.amount || 0)}</span></td>
                      <td>{tx.description || '—'}</td>
                      <td><span className="mono-text">{tx.invoice_no || '—'}</span></td>
                      <td>{tx.payment_method || '—'}</td>
                      <td><span className={`status-dot ${tx.status === 'success' ? 'active' : tx.status === 'pending' ? '' : 'inactive'}`}>{tx.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDocuments = () => (
    <div className="dash-section">
      <div className="stats-grid">
        {[
          { icon: '📄', label: 'Total Documents', value: (documents || []).length, color: '#6366f1' },
          { icon: '✅', label: 'Successful', value: (documents || []).filter(d => d.status === 'success').length, color: '#22c55e' },
          { icon: '❌', label: 'Failed', value: (documents || []).filter(d => d.status !== 'success').length, color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
            <div className="stat-card-glow" />
            <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
            <div className="stat-info">
              <span className="stat-value">{s.value}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="glass-panel">
        <div className="panel-head">
          <span className="panel-icon">📄</span><h3>Processed Documents</h3>
          <button className="tenant-add-btn" style={{ fontSize: '.78rem', padding: '6px 14px' }} onClick={fetchDocuments}>🔄 Refresh</button>
        </div>
        <div className="table-wrap" style={{ maxHeight: 500, overflow: 'auto' }}>
          <table className="dash-table" style={{ minWidth: 800 }}>
            <thead><tr><th>Time</th><th>Type</th><th>Category</th><th>Filename</th><th>Engine</th><th>Time (ms)</th><th>Confidence</th><th>Status</th></tr></thead>
            <tbody>
              {(documents || []).map((d, i) => (
                <tr key={i}>
                  <td><span className="text-muted">{d.created_at}</span></td>
                  <td><span className="feature-chip">{d.document_type}</span></td>
                  <td>{d.pdf_category || '—'}</td>
                  <td><span className="mono-text" style={{ fontSize: '.72rem' }}>{d.original_filename || '—'}</span></td>
                  <td>{d.engine_used || '—'}</td>
                  <td>{d.processing_time_ms || 0}</td>
                  <td>{d.confidence_score ? `${d.confidence_score}%` : '—'}</td>
                  <td><span className={`status-dot ${d.status === 'success' ? 'active' : 'inactive'}`}>{d.status}</span></td>
                </tr>
              ))}
              {(!documents || documents.length === 0) && <tr><td colSpan={8} className="no-data">No documents processed yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="dash-section">
      <div className="dash-grid-2">
        <div className="glass-panel">
          <div className="panel-head"><span className="panel-icon">🏢</span><h3>Account Info</h3></div>
          <div className="perf-metrics">
            <div className="perf-row"><span className="perf-label">Client ID</span><span className="perf-value mono-text">{clientId}</span></div>
            <div className="perf-row"><span className="perf-label">Name</span><span className="perf-value">{tenant.name || clientName}</span></div>
            <div className="perf-row"><span className="perf-label">Email</span><span className="perf-value">{tenant.contact_email || '—'}</span></div>
            <div className="perf-row"><span className="perf-label">Phone</span><span className="perf-value">{tenant.contact_phone || '—'}</span></div>
            <div className="perf-row"><span className="perf-label">Address</span><span className="perf-value">{tenant.company_address || '—'}</span></div>
            <div className="perf-row"><span className="perf-label">Status</span><span className={`status-dot ${tenant.active ? 'active' : 'inactive'}`}>{tenant.active ? 'Active' : 'Inactive'}</span></div>
          </div>
        </div>
        <div className="glass-panel">
          <div className="panel-head"><span className="panel-icon">⚙️</span><h3>Configuration</h3></div>
          <div className="perf-metrics">
            <div className="perf-row"><span className="perf-label">API Integration</span><span className={`status-dot ${tenant.api_integration_enabled !== false ? 'active' : 'inactive'}`}>{tenant.api_integration_enabled !== false ? '✅ Enabled' : '❌ Disabled'}</span></div>
            <div className="perf-row"><span className="perf-label">Features</span>
              <div className="feature-tags">{(tenant.features || []).map((f, i) => <span key={i} className="feature-chip">{f}</span>)}</div>
            </div>
            <div className="perf-row"><span className="perf-label">Summary — Key Points</span><span className={`status-dot ${tenant.summary_config?.include_key_points ? 'active' : 'inactive'}`}>{tenant.summary_config?.include_key_points ? 'Yes' : 'No'}</span></div>
            <div className="perf-row"><span className="perf-label">Summary — Full Text</span><span className={`status-dot ${tenant.summary_config?.include_full_text ? 'active' : 'inactive'}`}>{tenant.summary_config?.include_full_text ? 'Yes' : 'No'}</span></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderHelp = () => (
    <div className="dash-section">
      <div className="glass-panel">
        <div className="panel-head"><span className="panel-icon">❓</span><h3>Help & Documentation</h3></div>
        <div className="help-grid">
          <div className="help-item">
            <h4>🔐 Authentication</h4>
            <p>Send a POST request to <code>/auth</code> with your API key and client_name to get an access token. Use the token in the Authorization header as <code>Bearer &lt;token&gt;</code>.</p>
          </div>
          <div className="help-item">
            <h4>📄 Processing Documents</h4>
            <p>Send a POST request to <code>/process</code> with a file and document_type (PAN, AADHAAR, DL, VOTERID, PDF). For PDFs, include pdf_category (RESUME, NOTICE, SUMMARY, BANK_STATEMENT, etc).</p>
          </div>
          <div className="help-item">
            <h4>🔄 Token Refresh</h4>
            <p>Access tokens expire after 1 hour. Use the refresh token with <code>POST /refresh</code> to get a new access token without re-authenticating.</p>
          </div>
          <div className="help-item">
            <h4>📡 API Limits</h4>
            <p>Your API usage is tracked per billing cycle. Check the Overview tab for current usage. Contact support if you need a higher limit.</p>
          </div>
          <div className="help-item">
            <h4>📧 Support</h4>
            <p>For technical issues or plan upgrades, contact your account manager or email support.</p>
          </div>
        </div>
      </div>
    </div>
  );

  // ═══ DATA & REPORTS ═══
  const [parsedData, setParsedData] = useState(null);
  const [parsedSearch, setParsedSearch] = useState('');
  const [parsedFilter, setParsedFilter] = useState('ALL');
  const [parsedPage, setParsedPage] = useState(1);
  const PARSED_PER_PAGE = 20;

  const fetchParsedData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/client/parsed-data?client_id=${clientIdRef.current}`, { headers: { Authorization: `Bearer ${tokenRef.current}` } });
      const data = await res.json();
      if (!data.error) setParsedData(data);
    } catch { /* silent */ }
  }, []);

  const formatBytes = (b) => {
    if (!b) return '0 B';
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1048576).toFixed(1)} MB`;
  };

  const renderReports = () => {
    const records = parsedData?.records || [];
    const summary = parsedData?.summary || {};
    const filtered = records.filter(r => {
      if (parsedFilter !== 'ALL' && r.document_type !== parsedFilter) return false;
      if (parsedSearch) {
        const q = parsedSearch.toLowerCase();
        return (r.original_filename || '').toLowerCase().includes(q)
          || (r.document_type || '').toLowerCase().includes(q)
          || (r.pdf_category || '').toLowerCase().includes(q);
      }
      return true;
    });
    const totalPages = Math.ceil(filtered.length / PARSED_PER_PAGE) || 1;
    const page = Math.min(parsedPage, totalPages);
    const paged = filtered.slice((page - 1) * PARSED_PER_PAGE, page * PARSED_PER_PAGE);
    const docTypes = [...new Set(records.map(r => r.document_type))];

    return (
      <div className="dash-section">
        <div className="stats-grid">
          {[
            { icon: '📄', label: 'Total Records', value: summary.total || 0, color: '#6366f1' },
            { icon: '📦', label: 'Total Size', value: formatBytes(summary.total_size_bytes), color: '#22c55e' },
            { icon: '📁', label: 'Document Types', value: Object.keys(summary.by_type || {}).length, color: '#f59e0b' },
            { icon: '✅', label: 'Success Rate', value: records.length ? `${Math.round((records.filter(r => r.status === 'success').length / records.length) * 100)}%` : '—', color: '#8b5cf6' },
          ].map((s, i) => (
            <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
              <div className="stat-card-glow" />
              <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
              <div className="stat-info">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Type breakdown */}
        {summary.by_type && Object.keys(summary.by_type).length > 0 && (
          <div className="glass-panel" style={{ padding: 18, marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '.82rem', fontWeight: 700, color: 'rgba(255,255,255,.5)' }}>BY TYPE:</span>
              {Object.entries(summary.by_type).map(([dt, cnt]) => (
                <span key={dt} className="feature-chip">{dt}: {cnt}</span>
              ))}
            </div>
          </div>
        )}

        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">📊</span><h3>Parsed Documents</h3>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="text" placeholder="🔍 Search filename..." value={parsedSearch}
                onChange={e => { setParsedSearch(e.target.value); setParsedPage(1); }}
                className="modal-input" style={{ width: 180, padding: '6px 12px', fontSize: '.82rem' }} />
              <select className="modal-select" value={parsedFilter}
                onChange={e => { setParsedFilter(e.target.value); setParsedPage(1); }}
                style={{ width: 130, padding: '6px 10px', fontSize: '.82rem' }}>
                <option value="ALL">All Types</option>
                {docTypes.map(dt => <option key={dt} value={dt}>{dt}</option>)}
              </select>
              <button className="tenant-add-btn" style={{ fontSize: '.78rem', padding: '6px 14px' }} onClick={fetchParsedData}>🔄 Refresh</button>
            </div>
          </div>
          <div className="table-wrap" style={{ maxHeight: 500, overflow: 'auto' }}>
            <table className="dash-table" style={{ minWidth: 800 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Filename</th>
                  <th>Size</th>
                  <th>Time (ms)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r, i) => (
                  <tr key={r.id}>
                    <td className="text-muted">{(page - 1) * PARSED_PER_PAGE + i + 1}</td>
                    <td><span className="text-muted">{r.created_at}</span></td>
                    <td><span className="feature-chip">{r.document_type}</span></td>
                    <td>{r.pdf_category || '—'}</td>
                    <td><span className="mono-text" style={{ fontSize: '.72rem' }}>{r.original_filename || '—'}</span></td>
                    <td>{formatBytes(r.file_size_bytes)}</td>
                    <td>{r.processing_time_ms || 0}</td>
                    <td><span className={`status-dot ${r.status === 'success' ? 'active' : 'inactive'}`}>{r.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="tester-preset-btn" style={{ padding: '4px 8px', fontSize: '.72rem' }}
                          onClick={() => window.open(`${API}/client/parsed-data/${r.id}/json?client_id=${clientId}&download=true`, '_blank')}
                          title="Download JSON">💾 JSON</button>
                        <button className="tester-preset-btn" style={{ padding: '4px 8px', fontSize: '.72rem' }}
                          onClick={() => window.open(`${API}/client/parsed-data/${r.id}/text?client_id=${clientId}`, '_blank')}
                          title="Download Text">📄 TXT</button>
                        <button className="tester-preset-btn" style={{ padding: '4px 8px', fontSize: '.72rem' }}
                          onClick={() => window.open(`${API}/client/parsed-data/${r.id}/file?client_id=${clientId}`, '_blank')}
                          title="Download Original File">📥 File</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {paged.length === 0 && <tr><td colSpan={9} className="no-data">No parsed data yet. Process documents to see results here.</td></tr>}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '16px 0', alignItems: 'center' }}>
              <button className="tester-preset-btn" disabled={page <= 1} onClick={() => setParsedPage(p => p - 1)}>◀</button>
              <span className="text-muted" style={{ fontSize: '.85rem' }}>Page {page} of {totalPages} ({filtered.length} records)</span>
              <button className="tester-preset-btn" disabled={page >= totalPages} onClick={() => setParsedPage(p => p + 1)}>▶</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ═══ DOCUMENT PARSING ═══
  const docTypes = [
    { value: 'PAN', label: '🆔 PAN Card', icon: '🆔' },
    { value: 'AADHAAR', label: '🪪 Aadhaar Card', icon: '🪪', dual: true },
    { value: 'DRIVING', label: '🚗 Driving License', icon: '🚗' },
    { value: 'VOTER', label: '🗳️ Voter ID', icon: '🗳️' },
    { value: 'PDF', label: '📄 PDF Document', icon: '📄', hasCat: true },
  ];
  const pdfCats = [
    { value: 'RESUME', label: 'Resume / CV (ATS Scoring)' },
    { value: 'SUMMARY', label: 'AI Summary' },
    { value: 'NOTICEAI', label: 'Tax Notices / Legal' },
    { value: 'OTHEREVENTS', label: 'Other / General' },
    { value: 'BANK_STATEMENT', label: 'Bank Statement Analysis' },
  ];

  const handleParseFileChange = (fileObj, type) => {
    if (!fileObj) return;
    const isPDF = fileObj.name.toLowerCase().endsWith('.pdf');
    const max = isPDF ? 228 * 1024 * 1024 : 16 * 1024 * 1024;
    if (fileObj.size > max) { setParseError(`File too large. Max: ${isPDF ? '228 MB' : '16 MB'}`); return; }
    setParseError('');
    if (type === 'file') {
      setParseFile(fileObj);
      setParsePreviews(p => ({ ...p, file: fileObj.type.startsWith('image/') ? URL.createObjectURL(fileObj) : null }));
    } else if (type === 'front') {
      setParseFront(fileObj);
      setParsePreviews(p => ({ ...p, front: URL.createObjectURL(fileObj) }));
    } else {
      setParseBack(fileObj);
      setParsePreviews(p => ({ ...p, back: URL.createObjectURL(fileObj) }));
    }
  };

  const parseTokenRef = useRef('');

  const autoAuth = async () => {
    if (parseTokenRef.current) return parseTokenRef.current;
    const apiKey = profile?.client?.key;
    if (!apiKey) { setParseError('API key not loaded yet — please wait a moment and try again'); return null; }
    setParseStatus('Authenticating...');
    try {
      const res = await fetch(`${API}/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Portal-Internal': '1' },
        body: JSON.stringify({ Authorization: apiKey, client_name: clientIdRef.current }),
      });
      const data = await res.json();
      if (data.access_token) {
        parseTokenRef.current = data.access_token;
        setParseToken(data.access_token);
        setParseStatus('');
        return data.access_token;
      }
      throw new Error(data.error?.msg || 'Auth failed');
    } catch (e) { setParseError(e.message); setParseStatus(''); return null; }
  };

  const handleProcess = async () => {
    if (!parseDocType) { setParseError('Please select a document type first'); return; }
    if (parseDocType === 'AADHAAR' && (!parseFront || !parseBack)) { setParseError('Upload both front & back for Aadhaar'); return; }
    if (parseDocType !== 'AADHAAR' && !parseFile) { setParseError('Please select a file'); return; }
    setParseError(''); setParseResults(null); setParseLoading(true);
    const tkn = await autoAuth();
    if (!tkn) { setParseLoading(false); return; }
    setParseStatus('Processing document...');
    try {
      const fd = new FormData();
      fd.append('document_type', parseDocType);
      if (parseDocType === 'PDF') fd.append('pdf_category', parsePdfCat);
      if (parseDocType === 'AADHAAR') { fd.append('front', parseFront); fd.append('back', parseBack); }
      else fd.append('file', parseFile);
      const res = await fetch(`${API}/process`, { method: 'POST', headers: { Authorization: `Bearer ${tkn}` }, body: fd });
      const data = await res.json();
      if (res.ok) { setParseResults(data); fetchProfile(); fetchDocuments(); }
      else setParseError(data.error?.msg || 'Processing failed');
    } catch (e) { setParseError('Server error: ' + e.message); }
    setParseLoading(false); setParseStatus('');
  };

  const resetParsing = (keepDocType) => {
    setParseFile(null); setParseFront(null); setParseBack(null);
    setParsePreviews({ file: null, front: null, back: null });
    setParseResults(null); setParseError('');
    if (!keepDocType) setParseDocType('');
  };

  const renderParsing = () => {
    const features = tenant.features || [];
    const isTypeEnabled = (dt) => {
      if (dt.value === 'PDF') return features.some(f => ['RESUME','NOTICE','SUMMARY','BANK_STATEMENT'].includes(f));
      return features.includes(dt.value);
    };
    const isPdfCatEnabled = (cat) => {
      const map = { RESUME: 'RESUME', SUMMARY: 'SUMMARY', NOTICEAI: 'NOTICE', OTHEREVENTS: 'NOTICE', BANK_STATEMENT: 'BANK_STATEMENT' };
      return features.includes(map[cat] || '');
    };
    const anyEnabled = docTypes.some(dt => isTypeEnabled(dt));
    return (
      <div className="dash-section">
        {/* Header */}
        <div className="glass-panel" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.4rem' }}>🔍</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'rgba(255,255,255,.9)' }}>Document Parsing</h3>
              <span className="text-muted">Upload and process documents using AI-powered OCR</span>
            </div>
            <div className="feature-tags">
              {features.map((f, i) => <span key={i} className="feature-chip">{f}</span>)}
            </div>
          </div>
        </div>

        {!anyEnabled ? (
          <div className="glass-panel" style={{ padding: 40, textAlign: 'center' }}>
            <span style={{ fontSize: '3rem' }}>🔒</span>
            <p style={{ color: 'rgba(255,255,255,.5)', marginTop: 16 }}>No document processing features enabled for your account. Contact admin.</p>
          </div>
        ) : (<>
          {/* Step 1: Select Document Type */}
          <div className="glass-panel" style={{ padding: 24, marginBottom: 20 }}>
            <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 14 }}>
              <span className="panel-icon">📋</span><h3>Step 1 — Select Document Type</h3>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {docTypes.map(dt => {
                const enabled = isTypeEnabled(dt);
                return (
                  <button key={dt.value}
                    className={`tester-preset-btn ${parseDocType === dt.value ? 'active' : ''}`}
                    disabled={!enabled}
                    style={{
                      padding: '12px 20px', fontSize: '.92rem',
                      ...(parseDocType === dt.value ? { background: 'rgba(99,102,241,.15)', borderColor: 'rgba(99,102,241,.4)', color: '#a5b4fc' } : {}),
                      ...(!enabled ? { opacity: 0.35, cursor: 'not-allowed', filter: 'grayscale(1)' } : {}),
                    }}
                    title={!enabled ? 'Feature not enabled for your account' : ''}
                    onClick={() => { if (enabled && parseDocType !== dt.value) { setParseDocType(dt.value); resetParsing(true); } }}>
                    {dt.label}{!enabled && ' 🔒'}
                  </button>
                );
              })}
            </div>
            {!parseDocType && <p style={{ color: 'rgba(255,255,255,.35)', margin: '14px 0 0', fontSize: '.85rem' }}>👆 Select a document type to continue</p>}
          </div>

          {/* Step 2: Upload & Process (only after doc type selected) */}
          {parseDocType && (
            <div className="glass-panel" style={{ padding: 24, marginBottom: 20 }}>
              <div className="panel-head" style={{ marginBottom: 20, paddingBottom: 14 }}>
                <span className="panel-icon">📤</span><h3>Step 2 — Upload & Process</h3>
              </div>

              {parseError && (
                <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 12, marginBottom: 16, color: '#fca5a5', fontSize: '.88rem', fontWeight: 600 }}>
                  ⚠️ {parseError}
                </div>
              )}
              {parseStatus && (
                <div style={{ padding: '12px 16px', background: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.25)', borderRadius: 12, marginBottom: 16, color: '#a5b4fc', fontSize: '.88rem', fontWeight: 600 }}>
                  ⏳ {parseStatus}
                </div>
              )}

              {parseDocType === 'PDF' && (
                <div className="modal-field" style={{ marginBottom: 16 }}>
                  <label>PDF Category</label>
                  <select className="modal-select" value={parsePdfCat} onChange={e => setParsePdfCat(e.target.value)}>
                    {pdfCats.map(c => {
                      const catEnabled = isPdfCatEnabled(c.value);
                      return <option key={c.value} value={c.value} disabled={!catEnabled}>{c.label}{!catEnabled ? ' 🔒' : ''}</option>;
                    })}
                  </select>
                </div>
              )}

              {parseDocType === 'AADHAAR' ? (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                  <div className="modal-field" style={{ flex: 1, minWidth: 200 }}>
                    <label>🖼️ Front Side</label>
                    <label className="tester-file-input" style={{ display: 'block' }}>
                      <input type="file" accept="image/*" onChange={e => handleParseFileChange(e.target.files[0], 'front')} />
                      <span className="tester-file-label">{parseFront ? `📎 ${parseFront.name}` : '📁 Choose front image...'}</span>
                    </label>
                  </div>
                  <div className="modal-field" style={{ flex: 1, minWidth: 200 }}>
                    <label>🖼️ Back Side</label>
                    <label className="tester-file-input" style={{ display: 'block' }}>
                      <input type="file" accept="image/*" onChange={e => handleParseFileChange(e.target.files[0], 'back')} />
                      <span className="tester-file-label">{parseBack ? `📎 ${parseBack.name}` : '📁 Choose back image...'}</span>
                    </label>
                  </div>
                </div>
              ) : (
                <div className="modal-field" style={{ marginBottom: 16 }}>
                  <label>📁 Upload File</label>
                  <label className="tester-file-input" style={{ display: 'block' }}>
                    <input type="file" accept={parseDocType === 'PDF' ? '.pdf' : 'image/*,.pdf'}
                      onChange={e => handleParseFileChange(e.target.files[0], 'file')} />
                    <span className="tester-file-label">{parseFile ? `📎 ${parseFile.name} (${(parseFile.size / 1024).toFixed(1)} KB)` : '📁 Choose file...'}</span>
                  </label>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="tester-send-btn" style={{ borderRadius: 12, padding: '14px 32px' }}
                  onClick={handleProcess}
                  disabled={parseLoading || (parseDocType === 'AADHAAR' ? (!parseFront || !parseBack) : !parseFile)}>
                  {parseLoading ? '⏳ Processing...' : '🤖 Analyze Document'}
                </button>
                {parseResults && (
                  <button className="tester-preset-btn" onClick={() => resetParsing(false)} style={{ padding: '14px 20px' }}>🔄 New</button>
                )}
                <span className="text-muted" style={{ fontSize: '.78rem', marginLeft: 8 }}>
                  🖼️ Images: max 16 MB &nbsp;•&nbsp; 📄 PDFs: max 228 MB
                </span>
              </div>
            </div>
          )}
        </>)}

        {/* Results */}
        {parseLoading && <LoadingOverlay documentType={parseDocType} pdfCategory={parsePdfCat} />}

        {parseResults && (
          <div style={{ marginTop: 20 }}>
            <DocumentViewer
              results={parseResults}
              documentType={parseDocType}
              pdfCategory={parsePdfCat}
              previewUrls={parsePreviews}
              file={parseFile}
              frontFile={parseFront}
              backFile={parseBack}
            />
          </div>
        )}
      </div>
    );
  };

  // ═══ API TESTER HELPERS ═══
  const updateTesterHeader = (idx, field, val) => {
    setTester(p => { const h = [...p.headers]; h[idx] = { ...h[idx], [field]: val }; return { ...p, headers: h }; });
  };
  const addTesterHeader = () => setTester(p => ({ ...p, headers: [...p.headers, { key: '', value: '', enabled: true }] }));
  const removeTesterHeader = (idx) => setTester(p => ({ ...p, headers: p.headers.filter((_, i) => i !== idx) }));
  const updateFormField = (idx, field, val) => {
    setTester(p => {
      const ff = [...p.formFields]; ff[idx] = { ...ff[idx], [field]: val };
      if (field === 'type' && val === 'file') ff[idx].value = '';
      if (field === 'type' && val === 'text') ff[idx].file = null;
      return { ...p, formFields: ff };
    });
  };
  const addFormField = () => setTester(p => ({ ...p, formFields: [...p.formFields, { key: '', value: '', type: 'text', file: null }] }));
  const removeFormField = (idx) => setTester(p => ({ ...p, formFields: p.formFields.filter((_, i) => i !== idx) }));

  const sendTestRequest = async () => {
    setTester(p => ({ ...p, sending: true, response: null }));
    const start = performance.now();
    try {
      const hdrs = {};
      tester.headers.filter(h => h.enabled && h.key).forEach(h => { hdrs[h.key] = h.value; });
      const opts = { method: tester.method, headers: hdrs };
      if (['POST', 'PUT', 'PATCH'].includes(tester.method)) {
        if (tester.bodyType === 'form-data') {
          const fd = new FormData();
          tester.formFields.filter(f => f.key).forEach(f => {
            if (f.type === 'file' && f.file) fd.append(f.key, f.file, f.file.name);
            else if (f.type === 'text') fd.append(f.key, f.value || '');
          });
          opts.body = fd; delete opts.headers['Content-Type'];
        } else if (tester.body.trim()) { opts.body = tester.body; }
      }
      const res = await fetch(tester.url, opts);
      const elapsed = Math.round(performance.now() - start);
      const ct = res.headers.get('content-type') || '';
      let body = ct.includes('json') ? JSON.stringify(await res.json(), null, 2) : await res.text();
      const respHeaders = {}; res.headers.forEach((v, k) => { respHeaders[k] = v; });
      let lastToken = tester._lastToken;
      if (tester.url.endsWith('/auth') && res.status === 200) {
        try { const parsed = JSON.parse(body); if (parsed.access_token) lastToken = parsed.access_token; } catch {}
      }
      setTester(p => ({ ...p, sending: false, _lastToken: lastToken, response: {
        status: res.status, statusText: res.statusText, time: elapsed,
        size: new Blob([body]).size, body, headers: respHeaders,
      }}));
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      setTester(p => ({ ...p, sending: false, response: {
        status: 0, statusText: 'Network Error', time: elapsed, size: 0, body: err.message, headers: {},
      }}));
    }
  };

  const testerPresets = [
    { label: '🔐 Authenticate', method: 'POST', url: `${API}/auth`,
      headers: [{ key: 'Content-Type', value: 'application/json', enabled: true }],
      body: JSON.stringify({ Authorization: tenant.key || '', client_name: clientId || '' }, null, 2),
      bodyType: 'json' },
    { label: '🔄 Refresh Token', method: 'POST', url: `${API}/refresh`,
      headers: [{ key: 'Content-Type', value: 'application/json', enabled: true }],
      body: JSON.stringify({ refresh_token: '<paste_refresh_token>' }, null, 2),
      bodyType: 'json' },
    { label: '📋 Process PAN', method: 'POST', url: `${API}/process`,
      headers: [{ key: 'Authorization', value: '', enabled: true }],
      bodyType: 'form-data',
      formFields: [
        { key: 'file', value: '', type: 'file', file: null },
        { key: 'document_type', value: 'PAN', type: 'text', file: null },
      ] },
    { label: '🪪 Process Aadhaar', method: 'POST', url: `${API}/process`,
      headers: [{ key: 'Authorization', value: '', enabled: true }],
      bodyType: 'form-data',
      formFields: [
        { key: 'front', value: '', type: 'file', file: null },
        { key: 'back', value: '', type: 'file', file: null },
        { key: 'document_type', value: 'AADHAAR', type: 'text', file: null },
      ] },
    { label: '📄 Process PDF (Resume)', method: 'POST', url: `${API}/process`,
      headers: [{ key: 'Authorization', value: '', enabled: true }],
      bodyType: 'form-data',
      formFields: [
        { key: 'file', value: '', type: 'file', file: null },
        { key: 'document_type', value: 'PDF', type: 'text', file: null },
        { key: 'pdf_category', value: 'RESUME', type: 'text', file: null },
      ] },
    { label: '📄 Process PDF (Notice)', method: 'POST', url: `${API}/process`,
      headers: [{ key: 'Authorization', value: '', enabled: true }],
      bodyType: 'form-data',
      formFields: [
        { key: 'file', value: '', type: 'file', file: null },
        { key: 'document_type', value: 'PDF', type: 'text', file: null },
        { key: 'pdf_category', value: 'NOTICEAI', type: 'text', file: null },
      ] },
    { label: '🏦 Process Bank Statement', method: 'POST', url: `${API}/process`,
      headers: [{ key: 'Authorization', value: '', enabled: true }],
      bodyType: 'form-data',
      formFields: [
        { key: 'file', value: '', type: 'file', file: null },
        { key: 'document_type', value: 'PDF', type: 'text', file: null },
        { key: 'pdf_category', value: 'BANK_STATEMENT', type: 'text', file: null },
      ] },
    { label: '💚 Health Check', method: 'GET', url: `${API}/health`,
      headers: [], body: '', bodyType: 'json' },
  ];

  const applyPreset = (preset) => {
    const tkn = tester._lastToken;
    let hdrs = preset.headers?.length ? preset.headers.map(h => ({...h})) : [{ key: '', value: '', enabled: true }];
    hdrs = hdrs.map(h => h.key === 'Authorization' ? { ...h, value: `Bearer ${tkn || '<run_auth_first>'}` } : h);
    setTester(p => ({
      ...p, method: preset.method, url: preset.url, headers: hdrs,
      body: preset.body || '', bodyType: preset.bodyType || 'json',
      formFields: preset.formFields ? preset.formFields.map(f => ({...f})) : [{ key: '', value: '', type: 'text', file: null }],
      response: null,
    }));
  };

  const renderTester = () => {
    if (!apiEnabled) return (
      <div className="dash-section">
        <div className="glass-panel" style={{ padding: 50, textAlign: 'center' }}>
          <span style={{ fontSize: '3.5rem', display: 'block', marginBottom: 16 }}>🛠️</span>
          <h3 style={{ margin: '0 0 10px', fontSize: '1.2rem', color: 'rgba(255,255,255,.9)' }}>Developer Tools Unavailable</h3>
          <p style={{ color: 'rgba(255,255,255,.45)', margin: '0 0 20px', maxWidth: 420, marginInline: 'auto', lineHeight: 1.6 }}>API integration feature is not enabled for your account. Once enabled by admin, you’ll get access to the API tester, cURL commands, code examples, and full documentation.</p>
          <span className="feature-chip" style={{ background: 'rgba(239,68,68,.12)', color: '#fca5a5', fontSize: '.85rem', padding: '8px 18px' }}>🔒 Contact Admin to Enable</span>
        </div>
      </div>
    );
    const integrationEnabled = true;
    return (
      <div className="dash-section">
        {/* Status Banner */}
        <div className="glass-panel" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.4rem' }}>🧪</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'rgba(255,255,255,.9)' }}>API Integration Tester</h3>
              <span className="text-muted">Test your API integration directly from the dashboard</span>
            </div>
            <span className={`status-dot ${integrationEnabled ? 'active' : 'inactive'}`}>
              {integrationEnabled ? '✅ API Enabled' : '❌ API Disabled'}
            </span>
            <span className={`plan-badge ${tenant.plan || 'free'}`}>{(plan.label || (tenant.plan || 'free')).toUpperCase()}</span>
          </div>
        </div>


        {/* Credentials Quick View */}
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { icon: '🆔', label: 'Client ID', value: clientId, color: '#6366f1' },
            { icon: '🔑', label: 'API Key', value: tenant.key ? `${tenant.key.slice(0, 12)}...` : '—', color: '#f59e0b' },
            { icon: '🎫', label: 'Token', value: tester._lastToken ? `${tester._lastToken.slice(0, 12)}...` : 'Not yet', color: tester._lastToken ? '#22c55e' : '#64748b' },
            { icon: '📊', label: 'API Usage', value: `${(tenant.api_used || 0).toLocaleString()} / ${tenant.api_limit === -1 ? '∞' : (tenant.api_limit || 0).toLocaleString()}`, color: '#8b5cf6' },
          ].map((s, i) => (
            <div key={i} className="stat-glass-card" style={{ '--accent': s.color, padding: 16 }}>
              <div className="stat-card-glow" />
              <div className="stat-icon-wrap" style={{ width: 40, height: 40 }}><span className="stat-icon" style={{ fontSize: '1.1rem' }}>{s.icon}</span></div>
              <div className="stat-info">
                <span className="stat-value" style={{ fontSize: '1rem' }}>{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Workflow Guide */}
        <div className="glass-panel" style={{ padding: 18, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '.82rem', fontWeight: 700, color: 'rgba(255,255,255,.5)' }}>WORKFLOW:</span>
            <span className="feature-chip" style={{ background: 'rgba(34,197,94,.12)', color: '#86efac' }}>① Authenticate → Get Token</span>
            <span style={{ color: 'rgba(255,255,255,.2)' }}>→</span>
            <span className="feature-chip" style={{ background: 'rgba(99,102,241,.12)', color: '#a5b4fc' }}>② Process Document</span>
            <span style={{ color: 'rgba(255,255,255,.2)' }}>→</span>
            <span className="feature-chip" style={{ background: 'rgba(245,158,11,.12)', color: '#fcd34d' }}>③ Get Results</span>
            {tester._lastToken && <span className="feature-chip" style={{ background: 'rgba(34,197,94,.15)', color: '#86efac', marginLeft: 'auto' }}>✅ Token captured — presets auto-fill it</span>}
          </div>
        </div>

        {/* Tester Panel */}
        <div className="glass-panel api-tester-panel">
          <div className="panel-head">
            <span className="panel-icon">🚀</span>
            <h3>Request Builder</h3>
          </div>

          {/* Presets */}
          <div className="tester-presets">
            {testerPresets.map((pr, i) => (
              <button key={i} className="tester-preset-btn" onClick={() => applyPreset(pr)}>
                <span className={`api-method method-${pr.method.toLowerCase()}`}>{pr.method}</span> {pr.label}
              </button>
            ))}
          </div>

          {/* URL Bar */}
          <div className="tester-url-bar">
            <select className="tester-method-select" value={tester.method}
              onChange={e => setTester(p => ({ ...p, method: e.target.value }))}>
              {['GET','POST','PUT','PATCH','DELETE'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <input className="tester-url-input" type="text" value={tester.url}
              onChange={e => setTester(p => ({ ...p, url: e.target.value }))} placeholder="Enter request URL" />
            <button className="tester-send-btn" onClick={sendTestRequest} disabled={tester.sending}>
              {tester.sending ? '⏳ Sending...' : '🚀 Send'}
            </button>
          </div>

          {/* Request Tabs */}
          <div className="tester-tabs">
            {[['headers', '📋 Headers'], ['body', '📝 Body']].map(([k, l]) => (
              <button key={k} className={`tester-tab ${testerTab === k ? 'active' : ''}`}
                onClick={() => setTesterTab(k)}>{l}</button>
            ))}
          </div>

          {/* Headers */}
          {testerTab === 'headers' && (
            <div className="tester-headers">
              {tester.headers.map((h, i) => (
                <div key={i} className="tester-header-row">
                  <input type="checkbox" checked={h.enabled} onChange={e => updateTesterHeader(i, 'enabled', e.target.checked)} />
                  <input type="text" className="tester-hdr-key" placeholder="Key" value={h.key} onChange={e => updateTesterHeader(i, 'key', e.target.value)} />
                  <input type="text" className="tester-hdr-val" placeholder="Value" value={h.value} onChange={e => updateTesterHeader(i, 'value', e.target.value)} />
                  <button className="tester-hdr-del" onClick={() => removeTesterHeader(i)}>✕</button>
                </div>
              ))}
              <button className="tester-add-hdr" onClick={addTesterHeader}>+ Add Header</button>
            </div>
          )}

          {/* Body */}
          {testerTab === 'body' && (<>
            <div className="tester-body-type-bar">
              {[['json', 'JSON'], ['form-data', 'Form Data']].map(([k, l]) => (
                <button key={k} className={`tester-body-type-btn ${tester.bodyType === k ? 'active' : ''}`}
                  onClick={() => setTester(p => ({ ...p, bodyType: k }))}>{l}</button>
              ))}
              {tester.bodyType === 'form-data' && <span className="tester-body-hint">💡 Use type "file" to attach images/PDFs</span>}
            </div>
            {tester.bodyType === 'json' ? (
              <textarea className="tester-body-editor" rows={6} value={tester.body}
                onChange={e => setTester(p => ({ ...p, body: e.target.value }))}
                placeholder='{ "key": "value" }' spellCheck={false} />
            ) : (
              <div className="tester-form-fields">
                {tester.formFields.map((f, i) => (
                  <div key={i} className="tester-form-row">
                    <select className="tester-form-type" value={f.type} onChange={e => updateFormField(i, 'type', e.target.value)}>
                      <option value="text">Text</option><option value="file">File</option>
                    </select>
                    <input type="text" className="tester-hdr-key" placeholder="Key" value={f.key} onChange={e => updateFormField(i, 'key', e.target.value)} />
                    {f.type === 'text' ? (
                      <input type="text" className="tester-hdr-val" placeholder="Value" value={f.value} onChange={e => updateFormField(i, 'value', e.target.value)} />
                    ) : (
                      <label className="tester-file-input">
                        <input type="file" onChange={e => updateFormField(i, 'file', e.target.files[0] || null)} />
                        <span className="tester-file-label">{f.file ? `📎 ${f.file.name} (${(f.file.size / 1024).toFixed(1)} KB)` : '📁 Choose file...'}</span>
                      </label>
                    )}
                    <button className="tester-hdr-del" onClick={() => removeFormField(i)}>✕</button>
                  </div>
                ))}
                <button className="tester-add-hdr" onClick={addFormField}>+ Add Field</button>
              </div>
            )}
          </>)}

          {/* Response */}
          {tester.response && (
            <div className="tester-response">
              <div className="tester-resp-bar">
                <span className="tester-resp-status" style={{ color: statusColor(tester.response.status) }}>
                  {tester.response.status} {tester.response.statusText}
                </span>
                <span className="tester-resp-meta">⏱ {tester.response.time}ms</span>
                <span className="tester-resp-meta">📦 {tester.response.size > 1024 ? `${(tester.response.size / 1024).toFixed(1)} KB` : `${tester.response.size} B`}</span>
                <div style={{ flex: 1 }} />
                <div className="tester-tabs" style={{ marginBottom: 0 }}>
                  {[['body', 'Body'], ['headers', 'Headers']].map(([k, l]) => (
                    <button key={k} className={`tester-tab ${respTab === k ? 'active' : ''}`} onClick={() => setRespTab(k)}>{l}</button>
                  ))}
                </div>
              </div>
              {respTab === 'body' ? (
                <pre className="tester-resp-body">{tester.response.body}</pre>
              ) : (
                <div className="tester-resp-headers">
                  {Object.entries(tester.response.headers).map(([k, v]) => (
                    <div key={k} className="perf-row"><span className="perf-label">{k}</span><span className="perf-value">{v}</span></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Features Access */}
        <div className="glass-panel" style={{ marginTop: 20 }}>
          <div className="panel-head"><span className="panel-icon">🔐</span><h3>Your Enabled Features</h3></div>
          <div className="feature-checkboxes">
            {(tenant.features || []).map((f, i) => (
              <span key={i} className="feat-check checked" style={{ cursor: 'default' }}>✅ {f}</span>
            ))}
            {(!tenant.features || tenant.features.length === 0) && <span className="text-muted">No features enabled</span>}
          </div>
        </div>

        {/* ═══ DEVELOPER CURL & DOCS ═══ */}
        {integrationEnabled && (<>
          <div className="glass-panel" style={{ marginTop: 20 }}>
            <div className="panel-head"><span className="panel-icon">🛠️</span><h3>Developer — cURL Commands</h3></div>
            <p className="text-muted" style={{ margin: '0 0 16px', fontSize: '.82rem' }}>Ready-to-use cURL commands for your enabled features. Click 📋 to copy.</p>
            {(() => {
              const baseUrl = API;
              const key = tenant.key || '<YOUR_API_KEY>';
              const cid = clientId || '<YOUR_CLIENT_ID>';
              const curls = [
                { label: '🔐 Step 1 — Authenticate (Get Token)', always: true, curl: `curl -X POST ${baseUrl}/auth \\
  -H "Content-Type: application/json" \\
  -d '{"Authorization": "${key}", "client_name": "${cid}"}'` },
                { label: '🔄 Refresh Token', always: true, curl: `curl -X POST ${baseUrl}/refresh \\
  -H "Content-Type: application/json" \\
  -d '{"refresh_token": "<YOUR_REFRESH_TOKEN>"}'` },
                { label: '💚 Health Check', always: true, curl: `curl -X GET ${baseUrl}/health` },
                { label: '🆔 Process PAN Card', feature: 'PAN', curl: `curl -X POST ${baseUrl}/process \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -F "file=@/path/to/pan.jpg" \\
  -F "document_type=PAN"` },
                { label: '🪪 Process Aadhaar (Front + Back)', feature: 'AADHAAR', curl: `curl -X POST ${baseUrl}/process \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -F "front=@/path/to/aadhaar_front.jpg" \\
  -F "back=@/path/to/aadhaar_back.jpg" \\
  -F "document_type=AADHAAR"` },
                { label: '🚗 Process Driving License', feature: 'DRIVING', curl: `curl -X POST ${baseUrl}/process \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -F "file=@/path/to/dl.jpg" \\
  -F "document_type=DRIVING"` },
                { label: '🗳️ Process Voter ID', feature: 'VOTER', curl: `curl -X POST ${baseUrl}/process \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -F "file=@/path/to/voter.jpg" \\
  -F "document_type=VOTER"` },
                { label: '📄 Process PDF — Resume (ATS)', feature: 'RESUME', curl: `curl -X POST ${baseUrl}/process \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -F "file=@/path/to/resume.pdf" \\
  -F "document_type=PDF" \\
  -F "pdf_category=RESUME"` },
                { label: '📄 Process PDF — AI Summary', feature: 'SUMMARY', curl: `curl -X POST ${baseUrl}/process \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -F "file=@/path/to/document.pdf" \\
  -F "document_type=PDF" \\
  -F "pdf_category=SUMMARY"` },
                { label: '📄 Process PDF — Tax Notice / Legal', feature: 'NOTICE', curl: `curl -X POST ${baseUrl}/process \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -F "file=@/path/to/notice.pdf" \\
  -F "document_type=PDF" \\
  -F "pdf_category=NOTICEAI"` },
                { label: '📄 Process PDF — Other / General', feature: 'NOTICE', curl: `curl -X POST ${baseUrl}/process \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -F "file=@/path/to/document.pdf" \\
  -F "document_type=PDF" \\
  -F "pdf_category=OTHEREVENTS"` },
                { label: '🏦 Process PDF — Bank Statement', feature: 'BANK_STATEMENT', curl: `curl -X POST ${baseUrl}/process \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -F "file=@/path/to/statement.pdf" \\
  -F "document_type=PDF" \\
  -F "pdf_category=BANK_STATEMENT"` },
              ];
              const features = tenant.features || [];
              const visible = curls.filter(c => c.always || features.includes(c.feature));
              return visible.map((c, i) => (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: '.88rem', fontWeight: 700, color: 'rgba(255,255,255,.85)' }}>{c.label}</span>
                    <button className="tester-preset-btn" style={{ padding: '4px 12px', fontSize: '.75rem' }}
                      onClick={() => { navigator.clipboard.writeText(c.curl.replace(/\\\\/g, '\\')); }}>
                      📋 Copy
                    </button>
                  </div>
                  <pre style={{ margin: 0, padding: '14px 16px', background: 'rgba(0,0,0,.35)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, fontSize: '.78rem', color: '#a5b4fc', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6 }}>{c.curl}</pre>
                </div>
              ));
            })()}
          </div>

          {/* Quick Integration Guide */}
          <div className="glass-panel" style={{ marginTop: 20 }}>
            <div className="panel-head"><span className="panel-icon">📖</span><h3>Quick Integration Guide</h3></div>
            <div className="help-grid">
              <div className="help-item">
                <h4>① Authenticate</h4>
                <p>POST <code>/auth</code> with your API key &amp; client_name. You'll receive an <code>access_token</code> (expires 5 min) and a <code>refresh_token</code> (expires 7 days).</p>
              </div>
              <div className="help-item">
                <h4>② Process Documents</h4>
                <p>POST <code>/process</code> with <code>Authorization: Bearer &lt;token&gt;</code> header. Send file as <code>multipart/form-data</code> with <code>document_type</code> field. For PDFs, also include <code>pdf_category</code>.</p>
              </div>
              <div className="help-item">
                <h4>③ Handle Aadhaar</h4>
                <p>Aadhaar requires two files: <code>front</code> and <code>back</code> fields instead of <code>file</code>. Both must be images (PNG/JPG/TIFF/BMP).</p>
              </div>
              <div className="help-item">
                <h4>④ Refresh Token</h4>
                <p>When access token expires, POST <code>/refresh</code> with your <code>refresh_token</code> to get a new access token without re-authenticating.</p>
              </div>
              <div className="help-item">
                <h4>⑤ Error Handling</h4>
                <p>All errors return <code>{'{"error": {"code": 4xx, "msg": "..."}}'}</code>. Common: 401 (bad token), 403 (feature not enabled), 400 (missing fields).</p>
              </div>
              <div className="help-item">
                <h4>⑥ File Limits</h4>
                <p>Images: max <strong>16 MB</strong> (PNG, JPG, JPEG, TIFF, BMP). PDFs: max <strong>228 MB</strong>. Response is structured JSON with extracted data.</p>
              </div>
            </div>
          </div>

          {/* Python & Node.js Examples */}
          <div className="glass-panel" style={{ marginTop: 20 }}>
            <div className="panel-head"><span className="panel-icon">💻</span><h3>Code Examples</h3></div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: '.88rem', fontWeight: 700, color: 'rgba(255,255,255,.85)' }}>🐍 Python (requests)</span>
                <button className="tester-preset-btn" style={{ padding: '4px 12px', fontSize: '.75rem' }}
                  onClick={() => navigator.clipboard.writeText(`import requests\n\nBASE = "${API}"\n\n# Step 1: Authenticate\nauth = requests.post(f"{BASE}/auth", json={\n    "Authorization": "${tenant.key || '<YOUR_API_KEY>'}",\n    "client_name": "${clientId || '<YOUR_CLIENT_ID>'}"\n}).json()\ntoken = auth["access_token"]\n\n# Step 2: Process document\nresp = requests.post(f"{BASE}/process",\n    headers={"Authorization": f"Bearer {token}"},\n    files={"file": open("document.pdf", "rb")},\n    data={"document_type": "PDF", "pdf_category": "RESUME"}\n)\nprint(resp.json())`)}>📋 Copy</button>
              </div>
              <pre style={{ margin: 0, padding: '14px 16px', background: 'rgba(0,0,0,.35)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, fontSize: '.78rem', color: '#86efac', overflowX: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{`import requests

BASE = "${API}"

# Step 1: Authenticate
auth = requests.post(f"{BASE}/auth", json={
    "Authorization": "${tenant.key || '<YOUR_API_KEY>'}",
    "client_name": "${clientId || '<YOUR_CLIENT_ID>'}"
}).json()
token = auth["access_token"]

# Step 2: Process document
resp = requests.post(f"{BASE}/process",
    headers={"Authorization": f"Bearer {token}"},
    files={"file": open("document.pdf", "rb")},
    data={"document_type": "PDF", "pdf_category": "RESUME"}
)
print(resp.json())`}</pre>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: '.88rem', fontWeight: 700, color: 'rgba(255,255,255,.85)' }}>🟢 Node.js (fetch)</span>
                <button className="tester-preset-btn" style={{ padding: '4px 12px', fontSize: '.75rem' }}
                  onClick={() => navigator.clipboard.writeText(`const BASE = "${API}";\n\n// Step 1: Authenticate\nconst auth = await fetch(\`\${BASE}/auth\`, {\n  method: "POST",\n  headers: { "Content-Type": "application/json" },\n  body: JSON.stringify({\n    Authorization: "${tenant.key || '<YOUR_API_KEY>'}",\n    client_name: "${clientId || '<YOUR_CLIENT_ID>'}"\n  })\n}).then(r => r.json());\nconst token = auth.access_token;\n\n// Step 2: Process document\nconst form = new FormData();\nform.append("file", fs.createReadStream("document.pdf"));\nform.append("document_type", "PDF");\nform.append("pdf_category", "RESUME");\n\nconst result = await fetch(\`\${BASE}/process\`, {\n  method: "POST",\n  headers: { Authorization: \`Bearer \${token}\` },\n  body: form\n}).then(r => r.json());\nconsole.log(result);`)}>📋 Copy</button>
              </div>
              <pre style={{ margin: 0, padding: '14px 16px', background: 'rgba(0,0,0,.35)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, fontSize: '.78rem', color: '#fcd34d', overflowX: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{`const BASE = "${API}";

// Step 1: Authenticate
const auth = await fetch(\`\${BASE}/auth\`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    Authorization: "${tenant.key || '<YOUR_API_KEY>'}",
    client_name: "${clientId || '<YOUR_CLIENT_ID>'}"
  })
}).then(r => r.json());
const token = auth.access_token;

// Step 2: Process document
const form = new FormData();
form.append("file", fs.createReadStream("document.pdf"));
form.append("document_type", "PDF");
form.append("pdf_category", "RESUME");

const result = await fetch(\`\${BASE}/process\`, {
  method: "POST",
  headers: { Authorization: \`Bearer \${token}\` },
  body: form
}).then(r => r.json());
console.log(result);`}</pre>
            </div>
          </div>

          {/* API Reference Table */}
          <div className="glass-panel" style={{ marginTop: 20 }}>
            <div className="panel-head"><span className="panel-icon">📡</span><h3>API Reference</h3></div>
            <div className="table-wrap">
              <table className="dash-table" style={{ minWidth: 700 }}>
                <thead><tr><th>Method</th><th>Endpoint</th><th>Auth</th><th>Body</th><th>Description</th></tr></thead>
                <tbody>
                  {[
                    ['POST', '/auth', '—', 'JSON', 'Authenticate & get tokens'],
                    ['POST', '/refresh', '—', 'JSON', 'Refresh access token'],
                    ['POST', '/process', 'Bearer', 'Form-Data', 'Process a document (OCR)'],
                    ['GET', '/health', '—', '—', 'System health & capabilities'],
                  ].map(([m, ep, auth, body, desc], i) => (
                    <tr key={i}>
                      <td><span className={`api-method method-${m.toLowerCase()}`}>{m}</span></td>
                      <td><span className="mono-text">{ep}</span></td>
                      <td>{auth}</td>
                      <td>{body}</td>
                      <td>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>)}
      </div>
    );
  };

  const sectionMap = {
    overview: renderOverview,
    parsing: renderParsing,
    reports: renderReports,
    api: renderAPI,
    tester: renderTester,
    billing: renderBilling,
    documents: renderDocuments,
    settings: renderSettings,
    help: renderHelp,
  };

  return (
    <div className="admin-dashboard client-portal">
      <div className="dash-bg-gradient" />
      <div className="dash-bg-mesh" />

      <aside className={`dash-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-glass" />
        <div className="sidebar-content">
          <div className="sidebar-brand">
            <img src={griffsoLogo} alt="Griffso" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '50%', flexShrink: 0 }} />
            {!sidebarCollapsed && <span className="brand-text">Admin Pannel</span>}
          </div>
          <nav className="sidebar-nav">
            {navItems.map(item => (
              <button key={item.id} className={`nav-btn ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => setActiveSection(item.id)} title={item.label}>
                <span className="nav-btn-icon">{item.icon}</span>
                {!sidebarCollapsed && <span className="nav-btn-label">{item.label}</span>}
                {activeSection === item.id && <div className="nav-active-indicator" />}
              </button>
            ))}
          </nav>
          <div className="sidebar-footer">
            <button className="nav-btn logout-btn" onClick={handleLogout}>
              <span className="nav-btn-icon">🚪</span>
              {!sidebarCollapsed && <span className="nav-btn-label">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      <main className={`dash-main ${sidebarCollapsed ? 'expanded' : ''}`}>
        <header className="dash-topbar">
          <div className="topbar-glass" />
          <div className="topbar-content">
            <div className="topbar-left">
              <button className="collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                {sidebarCollapsed ? '☰' : '✕'}
              </button>
              <div className="topbar-title">
                <h2>{navItems.find(n => n.id === activeSection)?.icon} {navItems.find(n => n.id === activeSection)?.label}</h2>
              </div>
            </div>
            <div className="topbar-right">
              <div className="topbar-time">
                <span className="time-icon">🕐</span>
                <span>{currentTime.toLocaleTimeString()}</span>
              </div>
              <button className="refresh-btn" onClick={() => { fetchProfile(); fetchApiLogs(); fetchDocuments(); fetchTransactions(); fetchDailyUsage(); fetchParsedData(); }}>
                <span>🔄</span>
              </button>
              <div className="topbar-avatar">
                <span className="avatar-icon">👤</span>
                <span className="avatar-name">{clientName || clientId}</span>
              </div>
              <button className="topbar-logout-btn" onClick={handleLogout} title="Logout">🚪</button>
            </div>
          </div>
        </header>

        <div className="dash-body">
          {loading ? (
            <div className="dash-loader"><div className="loader-ring" /><p>Loading your dashboard...</p></div>
          ) : (
            sectionMap[activeSection]?.()
          )}
        </div>
      </main>
    </div>
  );
};

export default ClientDashboard;
